/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { defaults } from './schema/pg-config.defaults';
import { Context as RenderContext, render } from './schema/pg-config.render';
import { schema, uiSchema } from './schema/pg-config.schema';
import * as Types from './schema/pg-config.types';
import { deserialize } from './serde/pg-config.deserialize';
import { serialize } from './serde/pg-config.serialize';
import { normalize } from './validation/pg-config.normalize';
import { validate } from './validation/pg-config.validate';

export type Config = Types.Config;
export type Schema = typeof schema;
export type UiSchema = typeof uiSchema;

export { getUiSchemaOption } from './serde/pg-config.util';

export const use = (context: RenderContext) => {
  const rendered = render(context);
  return {
    schema: { raw: schema, rendered },
    uiSchema,

    /**
     * Generates a nested object (as required by JSON-schema) with default values based on the cluster resources.
     */
    defaults: () => defaults(rendered),

    /**
     * Takes a flat object (as required by the gateway) and turns it into a nested object (as required by JSON-schema).
     */
    deserialize: (data: Record<string, string>) => deserialize(data, rendered),

    /**
     * Takes a nested object (as required by JSON-schema), validates it,
     * and corrects any values exceeding their minimum/maximum bounds to their respective limits.
     */
    normalize: (config: Config) => normalize(config, rendered),

    /**
     * Takes a nested object (as required by JSON-schema) and turns it into a flat object (as required by the gateway).
     */
    serialize: (config: Config) => serialize(config),

    /**
     * Takes a nested object (as required by JSON-schema) and validates it.
     */
    validate: (config: Config) => validate(config, rendered),
  };
};
